import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import GenericSelect from '../../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import CheckboxInput from '../../../components/ui/CheckboxInput';
import TextInput from '../../../components/ui/TextInput';
import Button from '../../../components/ui/Button';
import { Badge, Grid } from '../../performance-operations/styles';
import { formatNumber, usePanel } from '../../performance-operations/config';
import EvidencePanel from './EvidencePanel';
import ReviewDrawer from './ReviewDrawer';
import {
  DIRECTIONS, MAX_QUESTIONS, REVIEW_STATUSES, fetchRows, fetchWorkspaceQuestions, signedDiff,
} from './config';

// Analiz E08 "Liste" — çalışma tablosu: önceki, önerilen, onaylanan puan, fark, gerekçe, öneren, onaylayan, durum ve kayıt
// sürümü; seçilebilir soru sütunları (yönetici değerlendirmesinin likert yanıtı, en çok 5) ve tablo kolon seçicisi. Süzgeçler:
// puan bandı (seviye), değişiklik yönü, gerekçe eksik, inceleme durumu, ad araması. Satır → kanıt paneli (sağ, 4 kolon);
// satır menüsü → inceleme notu çekmecesi. Başlık yalnız DataTable'da; sayfa tek kaydırma (tablo gövdesi sınırsız yükseklik).
const Filters = styled.div`
  grid-column: span 12;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
`;

const Field = styled.div`
  width: ${({ $w }) => $w || '200px'};
  max-width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ListCol = styled.div`
  grid-column: span 8;
  min-width: 0;

  @media (max-width: 1024px) {
    grid-column: span 12;
  }
`;

export default function WorkspaceListTab({
  sessionId, levels, sessionStatus, rowFilters, onRowFilters, refreshKey, onChanged,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [paging, setPaging] = useState({ pageNumber: 1, pageSize: 20 });
  const [questionIds, setQuestionIds] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [localRefresh, setLocalRefresh] = useState(0);
  const canReview = sessionStatus === 'draft' || sessionStatus === 'active';

  const key = JSON.stringify({ rowFilters, questionIds });
  const [lastKey, setLastKey] = useState(key);
  if (lastKey !== key) { setLastKey(key); setPaging((p) => ({ ...p, pageNumber: 1 })); }

  const list = usePanel(() => fetchRows(sessionId, rowFilters, questionIds, paging.pageNumber, paging.pageSize),
    [sessionId, key, paging.pageNumber, paging.pageSize, refreshKey, localRefresh], !!sessionId);
  const questionsPanel = usePanel(() => fetchWorkspaceQuestions(sessionId), [sessionId], !!sessionId);
  const questions = useMemo(() => questionsPanel.data || [], [questionsPanel.data]);
  const rows = useMemo(() => list.data?.rows || [], [list.data]);

  const questionLookup = useMemo(() => ({
    onSearch: async (q) => {
      const needle = (q || '').toLocaleLowerCase();
      return questions.filter((x) => !needle || x.text.toLocaleLowerCase().includes(needle)).map((x) => ({ id: x.id, name: x.text }));
    },
    onGetById: async (id) => {
      const found = questions.find((x) => x.id === id);
      return { id, name: found ? found.text : '' };
    },
  }), [questions]);

  const level = useCallback((label, value) => (label ? `${label}${value != null ? ` (${formatNumber(value, lang)})` : ''}` : '-'), [lang]);

  const columns = useMemo(() => {
    const cols = [
      { name: 'employeeName', label: t('performance.calibrationWorkspace.col.employee'), options: { filter: false, sort: false } },
      { name: 'departmentName', label: t('performance.calibrationWorkspace.col.department'), options: { filter: false, sort: false, display: false, customBodyRender: (v) => v || '-' } },
      { name: 'beforeLabel', label: t('performance.calibrationWorkspace.col.before'), options: { filter: false, sort: false, customBodyRenderLite: (i) => level(rows[i]?.beforeLabel, rows[i]?.beforeValue) } },
      { name: 'proposedLabel', label: t('performance.calibrationWorkspace.col.proposed'), options: { filter: false, sort: false, customBodyRenderLite: (i) => level(rows[i]?.proposedLabel, rows[i]?.proposedValue) } },
      { name: 'approvedLabel', label: t('performance.calibrationWorkspace.col.approved'), options: { filter: false, sort: false, customBodyRenderLite: (i) => level(rows[i]?.approvedLabel, rows[i]?.approvedValue) } },
      {
        name: 'difference', label: t('performance.calibrationWorkspace.col.difference'),
        options: {
          filter: false, sort: false,
          customBodyRenderLite: (i) => {
            const r = rows[i];
            if (!r || r.difference == null) return '-';
            const tone = r.direction === 'up' ? 'success' : r.direction === 'down' ? 'danger' : undefined;
            return <Badge $tone={tone}>{signedDiff(r.difference, (v) => formatNumber(v, lang))}</Badge>;
          },
        },
      },
      { name: 'justification', label: t('performance.calibrationWorkspace.col.justification'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
      { name: 'proposerName', label: t('performance.calibrationWorkspace.col.proposer'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
      { name: 'approverName', label: t('performance.calibrationWorkspace.col.approver'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
      {
        name: 'recordStatus', label: t('performance.calibrationWorkspace.col.status'),
        options: { filter: false, sort: false, customBodyRender: (v) => t(`performance.calibrationWorkspace.recordStatus.${v}`, { defaultValue: v }) },
      },
      { name: 'version', label: t('performance.calibrationWorkspace.col.version'), options: { filter: false, sort: false, display: false } },
      {
        name: 'reviewStatus', label: t('performance.calibrationWorkspace.col.review'),
        options: { filter: false, sort: false, customBodyRender: (v) => t(`performance.calibrationWorkspace.reviewStatus.${v}`, { defaultValue: v }) },
      },
      { name: 'systemAScore', label: t('performance.calibrationWorkspace.evidence.systemA'), options: { filter: false, sort: false, display: false, customBodyRender: (v) => (v == null ? '-' : formatNumber(v, lang)) } },
      { name: 'systemBScore', label: t('performance.calibrationWorkspace.evidence.systemB'), options: { filter: false, sort: false, display: false, customBodyRender: (v) => (v == null ? '-' : formatNumber(v, lang)) } },
    ];
    questionIds.forEach((qid) => {
      const q = questions.find((x) => x.id === qid);
      cols.push({
        name: `q_${qid}`, label: q ? q.text : qid,
        options: { filter: false, sort: false, customBodyRenderLite: (i) => { const v = rows[i]?.answers?.[qid]; return v == null ? '-' : formatNumber(v, lang); } },
      });
    });
    cols.push({
      name: 'recordId', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false, viewColumns: false,
        customBodyRenderLite: (i) => {
          const r = rows[i];
          if (!r) return null;
          const actions = [{ type: 'detail', label: t('performance.calibrationWorkspace.action.evidence'), onClick: () => setSelected(r.recordId) }];
          if (canReview) actions.push({ type: 'edit', label: t('performance.calibrationWorkspace.review.edit'), onClick: () => setReviewTarget(r) });
          return <OptionsMenu row={r} actions={actions} />;
        },
      },
    });
    return cols;
  }, [t, lang, rows, questionIds, questions, canReview, level]);

  const onTableChanged = useCallback((action, state) => {
    if (action === 'changePage' || action === 'changeRowsPerPage') setPaging({ pageNumber: (state.page || 0) + 1, pageSize: state.rowsPerPage || 20 });
  }, []);

  const set = (patch) => onRowFilters({ ...rowFilters, ...patch });
  const levelOptions = (levels || []).map((l) => ({ id: l.levelId || l.id, name: l.label }));
  const reviewSaved = () => { setReviewTarget(null); setLocalRefresh((x) => x + 1); onChanged?.(); };

  return (
    <>
      <Grid data-tab="list">
        <Filters>
          <Field>
            <GenericSelect label={t('performance.calibrationWorkspace.filter.level')} value={rowFilters.levelId || null} onChange={(v) => set({ levelId: v })} options={levelOptions} />
          </Field>
          <Field>
            <GenericSelect
              label={t('performance.calibrationWorkspace.filter.direction')} value={rowFilters.direction || null} onChange={(v) => set({ direction: v })}
              options={DIRECTIONS.map((d) => ({ id: d, name: t(`performance.calibrationWorkspace.direction.${d}`) }))}
            />
          </Field>
          <Field>
            <GenericSelect
              label={t('performance.calibrationWorkspace.filter.review')} value={rowFilters.reviewStatus || null} onChange={(v) => set({ reviewStatus: v })}
              options={REVIEW_STATUSES.map((s) => ({ id: s, name: t(`performance.calibrationWorkspace.reviewStatus.${s}`) }))}
            />
          </Field>
          <Field $w="220px">
            <TextInput label={t('performance.calibrationWorkspace.filter.search')} value={rowFilters.search || ''} debounceMs={350} onChange={(e) => set({ search: e.target.value })} />
          </Field>
          <CheckboxInput label={t('performance.calibrationWorkspace.filter.missingJustification')} checked={!!rowFilters.missingJustification} onChange={(v) => set({ missingJustification: v })} />
          <Field $w="320px">
            <AsyncGenericSelect
              label={t('performance.calibrationWorkspace.filter.questions', { max: MAX_QUESTIONS })}
              value={questionIds} isMulti minChars={0} compact
              onChange={(v) => setQuestionIds((v || []).slice(0, MAX_QUESTIONS))}
              {...questionLookup}
            />
          </Field>
          {Object.values(rowFilters).some(Boolean) ? (
            <Button variant="ghost" onClick={() => onRowFilters({})} data-action="clear-filters">{t('performance.operations.filter.reset')}</Button>
          ) : null}
        </Filters>
        <ListCol data-panel="workRows">
          <DataTable
            title={t('performance.calibrationWorkspace.list.title')}
            fetching={list.loading}
            columns={columns}
            data={list.error ? [] : rows}
            totalRowCount={list.data?.total || 0}
            pageIndex={paging.pageNumber - 1}
            pageSize={paging.pageSize}
            onTableChanged={onTableChanged}
            onRowClick={(r) => setSelected(r.recordId)}
            disableFilters
            tableBodyMaxHeight="none"
          />
          {list.error ? <Button variant="outline" onClick={list.retry} data-action="retry-rows">{t('performance.operations.panel.retry')}</Button> : null}
        </ListCol>
        <EvidencePanel
          sessionId={sessionId} recordId={selected} refreshKey={`${refreshKey}-${localRefresh}`}
          canReview={canReview} onReview={(e) => setReviewTarget(e)}
        />
      </Grid>
      <ReviewDrawer sessionId={sessionId} target={reviewTarget} onClose={() => setReviewTarget(null)} onSaved={reviewSaved} />
    </>
  );
}
